import { ReactNode } from 'react';

import { ChatEvent } from '../ui/ChatEvent';
import { ChatMessage } from '../ui/ChatMessage/ChatMessage';
import { InteractiveMessage } from '../ui/InteractiveMessage';
import { ListPickerSelection } from '../ui/ListPicker/ListPicker';
import {
  AnyItemTypeChecker,
  isEvent,
  isGroupable,
  isAttachment,
  isInteractiveMessage,
  isListPickerSelection,
  isParticipantMessage
} from './itemCheckers';
import { IChatProps } from '../ui/Chat';
import { ChatMessageAttachment } from '../ui/Attachment';

/** For rendering a ChatTranscriptItem with awareness of its containing transcript. */
type ItemRenderDetails = {
  item: connect.ChatTranscriptItem;
  index: number;
  transcript: connect.ChatTranscriptItem[];
  downloadAttachment: IChatProps['downloadAttachment'];
};
type ItemRenderFunction = (itemDetails: ItemRenderDetails) => ReactNode;

type TranscriptItemRenderer = {
  /** The condition under which to select this renderer */
  test: AnyItemTypeChecker;
  render: ItemRenderFunction;
};

const renderListPickerSelection: ItemRenderFunction = ({ item }) => {
  return <ListPickerSelection item={item} />;
};

const renderChatMessage: ItemRenderFunction = ({ item, index, transcript }) => {
  const prevItem = transcript[index - 1];
  const nextItem = transcript[index + 1];
  const groupsWithPrev = isGroupable(item, prevItem);
  const groupsWithNext = isGroupable(item, nextItem);

  return <ChatMessage item={item} groupsWithNext={groupsWithNext} groupsWithPrev={groupsWithPrev} />;
};

const renderChatAttachment: ItemRenderFunction = ({ item, downloadAttachment }) => {
  return <ChatMessageAttachment item={item} downloadAttachment={downloadAttachment} />;
};

const renderInteractiveMessage: ItemRenderFunction = ({ item, index, transcript }) => {
  const isLast = index === transcript.length - 1;
  return <InteractiveMessage item={item} showOptions={isLast} />;
};

const renderEvent: ItemRenderFunction = ({ item }) => {
  return <ChatEvent item={item} />;
};

const RENDERERS: TranscriptItemRenderer[] = [
  {
    test: isListPickerSelection,
    render: renderListPickerSelection
  },
  {
    test: isParticipantMessage,
    render: renderChatMessage
  },
  {
    test: isAttachment,
    render: renderChatAttachment
  },
  {
    test: isInteractiveMessage,
    render: renderInteractiveMessage
  },
  {
    test: isEvent,
    render: renderEvent
  }
];

export const renderTranscriptItem = ({ item, index, transcript, downloadAttachment }: ItemRenderDetails) => {
  const renderer = RENDERERS.find(r => r.test(item, index, transcript));
  return renderer?.render({ item, index, transcript, downloadAttachment }) || null;
};
